/**
 * SaveRecipeButton — Erkennt Rezepte in Coach-Nachrichten und bietet
 * einen "Rezept speichern"-Button an.
 *
 * Erkennung: Prüft ob der Text typische Rezept-Marker enthält
 * (Zutaten, Zubereitung, Portionen etc.)
 * Speicherung: Ruft recipes.saveFromChat auf (LLM-Extraktion → DB)
 */
import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { BookmarkPlus, Check, Loader2, ChefHat } from "lucide-react";

/* ── Rezept-Erkennung ──────────────────────────────────────── */

/**
 * Heuristik: Prüft ob ein Text wahrscheinlich ein Rezept enthält.
 * Mindestens 2 von 5 Marker-Kategorien müssen zutreffen.
 */
export function looksLikeRecipe(text: string): boolean {
  if (!text || text.length < 80) return false;

  const lower = text.toLowerCase();
  let score = 0;

  // Kategorie 1: Zutaten-Marker
  if (/zutat(en)?[:\s]/i.test(text) || /ingredients?[:\s]/i.test(text)) score++;

  // Kategorie 2: Zubereitungs-Marker
  if (
    /zubereitung[:\s]/i.test(text) ||
    /anleitung[:\s]/i.test(text) ||
    /schritte?[:\s]/i.test(text) ||
    /instructions?[:\s]/i.test(text) ||
    /preparation[:\s]/i.test(text)
  ) score++;

  // Kategorie 3: Mengenangaben (z.B. "200g", "1 EL", "2 TL", "½ Tasse")
  if (/\d+\s*(g|ml|el|tl|tasse|prise|stück|bund|scheibe)/i.test(text)) score++;

  // Kategorie 4: Koch-Verben
  const cookVerbs = ["kochen", "braten", "backen", "dünsten", "rösten", "schneiden",
    "rühren", "mischen", "würzen", "anbraten", "köcheln", "aufkochen",
    "servieren", "garnieren", "erhitzen", "schälen"];
  if (cookVerbs.some((v) => lower.includes(v))) score++;

  // Kategorie 5: Portionen / Zubereitungszeit
  if (/portion(en)?[:\s]/i.test(text) || /für\s+\d+\s+person/i.test(text) ||
    /zubereitungszeit[:\s]/i.test(text) || /\d+\s*min/i.test(text)) score++;

  return score >= 2;
}

/* ── Mahlzeit-Typ erraten ──────────────────────────────────── */

function guessMealType(text: string): "fruehstueck" | "mittag" | "abend" | "snack" | "getraenk" {
  const lower = text.toLowerCase();
  if (/frühstück|porridge|müsli|overnight|smoothie.*bowl/i.test(lower)) return "fruehstueck";
  if (/abend(essen)?|dinner|suppe.*abend/i.test(lower)) return "abend";
  if (/snack|zwischenmahlzeit|energy.?ball|riegel/i.test(lower)) return "snack";
  if (/getränk|tee|latte|milch|golden.?milk|chai|lassi|saft/i.test(lower)) return "getraenk";
  return "mittag"; // Default
}

/* ── Komponente ────────────────────────────────────────────── */

interface SaveRecipeButtonProps {
  messageText: string;
  clientId: string;
  messageId: string;
}

export default function SaveRecipeButton({ messageText, clientId, messageId }: SaveRecipeButtonProps) {
  const isRecipe = useMemo(() => looksLikeRecipe(messageText), [messageText]);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveFromChat = trpc.recipes.saveFromChat.useMutation({
    onSuccess: (data) => {
      setSaved(true);
      toast.success(`"${data.title}" wurde in deinen Rezepten gespeichert!`, {
        description: `Kategorie: ${data.mealType}`,
        duration: 4000,
      });
    },
    onError: (err) => {
      setSaving(false);
      if (err.message.includes("Kein Rezept")) {
        toast.error("Kein Rezept erkannt — bitte versuche es mit einer anderen Nachricht.");
      } else {
        toast.error("Rezept konnte nicht gespeichert werden. Bitte versuche es erneut.");
      }
    },
  });

  if (!isRecipe) return null;

  const handleSave = () => {
    if (saved || saving) return;
    setSaving(true);
    const mealType = guessMealType(messageText);
    saveFromChat.mutate({
      clientId,
      messageContent: messageText,
      mealType,
    });
  };

  return (
    <button
      onClick={handleSave}
      disabled={saved || saving}
      className={`mt-2 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${
        saved
          ? "bg-[#6b7f4e]/15 text-[#6b7f4e] cursor-default"
          : saving
            ? "bg-[#6b7f4e]/10 text-[#6b7f4e]/60 cursor-wait"
            : "bg-[#6b7f4e]/8 text-[#6b7f4e] hover:bg-[#6b7f4e]/15 hover:shadow-sm active:scale-[0.97] cursor-pointer border border-[#6b7f4e]/20"
      }`}
    >
      {saved ? (
        <>
          <Check className="w-3.5 h-3.5" />
          Rezept gespeichert
        </>
      ) : saving ? (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Wird gespeichert...
        </>
      ) : (
        <>
          <ChefHat className="w-3.5 h-3.5" />
          Rezept speichern
        </>
      )}
    </button>
  );
}
